import {
  useQueryStates,
  parseAsString,
  parseAsArrayOf,
  parseAsIsoDate,
  createParser,
} from 'nuqs';
import { useMemo, useCallback } from 'react';
import type {
  CodingSearchParams,
  ClinicBranchFilter,
} from '../components/CodingSearchForm';
import { ALL_CODING_TAG_KEYS } from '../types/codingData.types';
import type { CodingTags, CodingTagTypes } from '../types/codingData.types';

// Custom parser for CodingTags (serializes as "archived:false,EMR_filled:true")
const parseAsCodingTags = createParser({
  parse: (value: string): CodingTags | null => {
    if (!value) return null;
    const tags: CodingTags = {};
    for (const part of value.split(',')) {
      const [key, val] = part.split(':');
      if (ALL_CODING_TAG_KEYS.includes(key as CodingTagTypes)) {
        tags[key as CodingTagTypes] = val === 'true';
      }
    }
    return Object.keys(tags).length > 0 ? tags : null;
  },
  serialize: (value: CodingTags): string => {
    return ALL_CODING_TAG_KEYS.filter(k => value[k] !== undefined)
      .map(k => `${k}:${value[k]}`)
      .join(',');
  },
  eq: (a: CodingTags, b: CodingTags): boolean =>
    ALL_CODING_TAG_KEYS.every(k => a[k] === b[k]),
});

// Custom parser for clinic_branches
// Serializes as "ClinicA:b1~b2|ClinicB:b3" — pipe separates clinics, tilde separates branch IDs
// A clinic with no branches is just "ClinicA:" (colon with empty right side)
const parseAsClinicBranches = createParser({
  parse: (value: string): ClinicBranchFilter[] | null => {
    if (!value) return null;
    const result: ClinicBranchFilter[] = [];
    for (const part of value.split('|')) {
      const colonIdx = part.indexOf(':');
      if (colonIdx === -1) continue;
      const clinic = decodeURIComponent(part.slice(0, colonIdx));
      const branchStr = part.slice(colonIdx + 1);
      const branches = branchStr
        ? branchStr.split('~').map(decodeURIComponent)
        : [];
      if (clinic) result.push({ clinic, branches });
    }
    return result.length > 0 ? result : null;
  },
  serialize: (value: ClinicBranchFilter[]): string => {
    return value
      .map(
        cb =>
          `${encodeURIComponent(cb.clinic)}:${cb.branches.map(encodeURIComponent).join('~')}`
      )
      .join('|');
  },
  eq: (a: ClinicBranchFilter[], b: ClinicBranchFilter[]): boolean => {
    if (a.length !== b.length) return false;
    return a.every(
      (ca, i) =>
        ca.clinic === b[i].clinic &&
        ca.branches.length === b[i].branches.length &&
        ca.branches.every((br: string, j: number) => br === b[i].branches[j])
    );
  },
});

// Helper to get date 3 weeks ago
const getThreeWeeksAgo = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() - 21);
  return date;
};

// URL param parsers configuration
const searchParamsParsers = {
  phrase: parseAsString.withDefault(''),
  start_date: parseAsIsoDate,
  end_date: parseAsIsoDate,
  coding_status: parseAsArrayOf(parseAsString).withDefault([]),
  visit_types: parseAsArrayOf(parseAsString).withDefault([]),
  clinic_branches: parseAsClinicBranches,
  tags: parseAsCodingTags,
};

/**
 * Hook to sync CodingSearchParams with URL query parameters using nuqs.
 * nuqs is the single source of truth - no separate useState needed.
 */
export function useCodingSearchParams() {
  const [urlParams, setUrlParams] = useQueryStates(searchParamsParsers, {
    history: 'replace',
  });

  // Check if URL has any params set
  const hasUrlParams =
    urlParams.start_date !== null ||
    urlParams.end_date !== null ||
    urlParams.phrase !== '' ||
    urlParams.coding_status.length > 0 ||
    urlParams.visit_types.length > 0 ||
    urlParams.clinic_branches !== null ||
    urlParams.tags !== null;

  // Convert nuqs params to CodingSearchParams format with defaults
  const searchParams = useMemo(
    (): CodingSearchParams => ({
      phrase: urlParams.phrase,
      start_date:
        urlParams.start_date ?? (hasUrlParams ? null : getThreeWeeksAgo()),
      end_date: urlParams.end_date,
      coding_status: urlParams.coding_status,
      visit_types: urlParams.visit_types,
      clinic_branches: urlParams.clinic_branches ?? [],
      tags: urlParams.tags ?? (hasUrlParams ? undefined : { archived: false }),
    }),
    [urlParams, hasUrlParams]
  );

  // Update URL params from CodingSearchParams
  const setSearchParams = useCallback(
    (params: CodingSearchParams) => {
      setUrlParams({
        phrase: params.phrase || null,
        start_date: params.start_date,
        end_date: params.end_date,
        coding_status:
          params.coding_status.length > 0 ? params.coding_status : null,
        visit_types: params.visit_types.length > 0 ? params.visit_types : null,
        clinic_branches:
          params.clinic_branches.length > 0 ? params.clinic_branches : null,
        tags:
          params.tags && Object.keys(params.tags).length > 0
            ? params.tags
            : null,
      });
    },
    [setUrlParams]
  );

  // Clear all URL params
  const clearSearchParams = useCallback(() => {
    setUrlParams({
      phrase: null,
      start_date: null,
      end_date: null,
      coding_status: null,
      visit_types: null,
      clinic_branches: null,
      tags: null,
    });
  }, [setUrlParams]);

  return {
    /** Current search params (derived from URL) */
    searchParams,
    /** Update search params (syncs to URL) */
    setSearchParams,
    /** Clear all search params from URL */
    clearSearchParams,
  };
}
